import React from 'react';
import './ChatUser.scss';
import { User } from '../../models/User';
import { store } from '../../app/store';
import { UserAccountRoleEnum, UserRelationRoleEnum } from '../../api/types';
import { UserDisplay } from '../user-display/UserDisplay';
import { DossierMember } from '../../models/DossierMember';
import { Badge } from '../badge/Badge';
import { DossierGroup, EDossierGroupType } from '../../models/DossierGroup';

interface ChatUserProps {
    members?: (DossierMember|User)[];
    dossierGroup: DossierGroup;
    small?: boolean;
    unreadCount?: number;
    className?: string;
}

function getUser(member: DossierMember|User): User {
  return member instanceof DossierMember ? member.user : member;
}

function filterOutThisUser(members: (DossierMember|User)[]): (DossierMember|User)[] {
  const thisUser = store.getState().session?.user;
  if (thisUser) {
    return members.filter((member => {
      const user = getUser(member);
      return !user.match(thisUser);
    }));
  } else {
    return [...members];
  }
}

function getChatRolesAsString(members: (DossierMember|User)[]): string {
  const rolesText: { [text: string]: boolean } = {};
  members.forEach(member => {
    let pluralize = true;
    if (member instanceof DossierMember && member.role === UserRelationRoleEnum.client) {
      pluralize = false;
    }
    if (member instanceof User && member.accountRole === UserAccountRoleEnum.client) {
      pluralize = false;
    }
    rolesText[member.getRoleText({ pluralize })] = true;
  });
  const text = Object.keys(rolesText).join(', ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function getChatTitle(members: (DossierMember|User)[], dossierGroup: DossierGroup): string {
  if (dossierGroup.type === EDossierGroupType.oneToAdmin) {
    /*
    // I don't think we want this, since the user will be displayed in the subtitle and else stuff gets messy.
    const usersToShow = users.filter(u => u.accountRole !== UserAccountRoleEnum.adminCaregiver && u.accountRole !== UserAccountRoleEnum.administration);
    if (usersToShow.length) {
      return `${usersToShow.map(user => user.name).join(', ')}, Administratie`;
    }
    */
    return 'Administratie';
  } else if (dossierGroup.type === EDossierGroupType.custom) {
    return dossierGroup.name;
  }
  if (members.length === 1) {
    const [member] = members;
    return member instanceof DossierMember ? member.user.name : member.name;
  } else {
    return getChatRolesAsString(members);
  }
}

function getChatSubTitle(members: (DossierMember|User)[], dossierGroup: DossierGroup): string {
  if (dossierGroup.type === EDossierGroupType.oneToAdmin) {
    const membersToShow = members.filter(member => {
      const user = getUser(member);
      return user.accountRole !== UserAccountRoleEnum.adminCaregiver && user.accountRole !== UserAccountRoleEnum.administration;
    });
    if (membersToShow.length) {
      return `Jij, ${membersToShow.map(member => (member instanceof DossierMember ? member.user.name : member.name)).join(', ')}, Administratie`;
    }
    return 'Jij, administratoren';
  }
  return `Jij, ${members.map(member => getUser(member).name).join(', ')}`;
}

function renderUser(members: (DossierMember|User)[], dossierGroup: DossierGroup, small?: boolean): JSX.Element {
  const withoutThisUser = filterOutThisUser(members);
  const title = getChatTitle(withoutThisUser, dossierGroup);
  const subTitle = getChatSubTitle(withoutThisUser, dossierGroup);
  if (withoutThisUser.length === 1) {
    return <UserDisplay user={withoutThisUser[0]} title={title} subTitle={subTitle} small={small} />;
  } else if (dossierGroup.type === EDossierGroupType.oneToAdmin) {
    return <UserDisplay icon="adminGroup" title={title} subTitle={subTitle} small={small} className="user-display--admin-group" />;
  } else {
    return <UserDisplay icon="group" title={title} subTitle={subTitle} small={small} />;
  }
}

export function ChatUsers(props: ChatUserProps): JSX.Element {
  const { members, dossierGroup, small, unreadCount, className } = props;
  return (
    <div className={`chat-users ${className || ''}`}>
      { renderUser(members || dossierGroup.users, dossierGroup, small) }
      <Badge count={unreadCount} className="chat-users__badge"/>
    </div>
  );
}
